// What the co-pilot looks like, without a call (Mike, 2026-09-20: "I still have no idea what the
// meeting bot co-pilot looks like"). /copilot?example draws the panel with the cards below and asks
// the server for nothing, so it opens on any device, signed in or not, and can be shown to anyone.
//
// Every word here is made up: the client is "Example Client", the rate objection and the answer are
// written for this page, and no line comes from a real call, a real account or any playbook. The panel
// says so at the top of the page, because a screen that looks exactly like the live one must never be
// taken for it.
import type { AskResult, Store } from '../api.js';
import type { CopilotCall, CopilotCard } from './api.js';
import { panelStart, type PanelState } from './panel-state.js';

const AT = '2026-09-20T10:33:00Z';

/** The store the example is drawn for: no tenant and no login stand behind it, so nothing may be asked of the server in its name. */
export const EXAMPLE_STORE: Store = { tenantId: 'example', kind: 'commercial', name: 'Example', principalId: 'example', role: 'user' };

const call: CopilotCall = {
  botId: 'example', company: { id: 'example', name: 'Example Client' }, meetingTitle: 'Pricing follow-up',
  state: 'in_call', subCode: null, startsAt: AT, listening: true, limit: null, recordingLocally: true, sharing: false,
};

const cards: CopilotCard[] = [
  {
    id: 'x3', kind: 'fact', shownAt: AT, outcome: null, heard: 'How many matches did we take last month?',
    fact: { text: '1,240 matches delivered in August, 96% of them live', source: 'Usage' },
  },
  {
    id: 'x1', kind: 'answer', shownAt: AT, outcome: null,
    heard: 'Honestly, the rate is too high for the volume we would actually use.',
    objection: { entryId: 'x-o', body: 'Your rate is too high' },
    reply: {
      entryId: 'x-r',
      body: 'The rate covers video, data and odds in one feed, so it replaces two contracts, not one. If volume is the issue, look at the tier, not the rate.',
      playbook: 'Example playbook', version: 2, approvedBy: 'an approver', approvedAt: '2026-09-16T09:00:00Z',
    },
  },
  {
    id: 'x2', kind: 'notes', shownAt: AT, outcome: null, objection: { entryId: 'x-o', body: 'Your rate is too high' },
    lines: [
      { text: 'The same point was answered with the volume tier on another call last week; they moved to the lower tier.',
        source: { kind: 'call', label: 'Call, a colleague, “another client”', at: '2026-09-11T09:00:00Z', signalId: 'x-s1' }, otherClient: null },
      { text: 'Their contract has a lower tier at 500 matches a month.',
        source: { kind: 'other', label: 'Contract register', at: null, signalId: 'x-s2' }, otherClient: 'another client' },
    ],
  },
];

export const EXAMPLE: PanelState = { ...panelStart, phase: 'ready', enabled: true, call, cards };

/** The line above the panel, so nobody takes the example for a call. */
export const EXAMPLE_WORDS = 'An example of the co-pilot beside a call. Nothing here is real: no client, no call, no playbook.';

// What the example's ask box answers with. The box is the live one, and the live one asks the ask bar's lane as a signed-in person of
// a real store; the example has neither, so until 2026-09-21 a question typed here went to the server all the same and came back "no
// store for this login", or sent a signed-out visitor to sign in. Now the example answers by itself, in the lane's shape, so the same
// drawing is exercised: paragraphs, what was read, what was not. Each answer says first that it is an example and that it is the same
// whatever is typed, and never that "nothing was read", since what was read is listed at its foot. The rest is as made up as the
// cards above and says nothing they do not already say: the same "another client", the same tier, the same August figure.
//
// The second answer carries the line Cobrain puts under an answer its own check took a sentence from (module 05 R49), so that line
// can be shown to someone without asking a real store about a subject Cobrain never writes about. That answer's first words say the
// line is part of the example, because under an innocent question it otherwise reads as a verdict on what was typed. Its words are the lane's
// (`leftOutNote(1)` in apps/api/src/ask/lane.ts): the example stands in for the lane, so this is the one place the portal writes them,
// and a test holds them to the words the panel's own drawing is tested with.
//
// A source's line is written as the lane writes one (`signalLine`, `shortDate`: "11 Sep"), which is how the panel's cards spell the same
// day: both write a month from the one table ("Sep"), never from Intl, whose "Sept" once set the cards apart from the answer under them.
// A test holds this line's day to the cards' own `dayOf`, so the example cannot drift from either.
const LEFT_OUT_ONE = 'One sentence was left out of this answer: it touched a subject Cobrain never writes about.';

type ExampleAnswer = Pick<AskResult, 'answer' | 'leftOut' | 'sources' | 'notRead'>;

export const EXAMPLE_ANSWERS: readonly ExampleAnswer[] = [
  {
    answer: 'This is an example. Your question went nowhere: whatever you type, you get this made-up answer, sources and all.\n\n'
      + 'The rate came up on another call last week. It was answered with the volume tier, and that client moved to the lower tier, which their contract sets at 500 matches a month.',
    sources: [
      { kind: 'call', who: 'a colleague', at: '2026-09-11T09:00:00Z', line: 'Call · a colleague · 11 Sep', signalId: null },
      { kind: 'Record', who: 'Contract', at: null, line: 'Record · Contract · another client', signalId: null },
    ],
    notRead: ['WhatsApp: not connected for you, so not read.'],
  },
  {
    answer: 'An example again. Nothing was sent, and nothing was really left out: the line under this answer is what you see when Cobrain leaves a sentence out of a real one.\n\n'
      + 'Example Client took 1,240 matches in August, 96% of them live.\n\n'
      + LEFT_OUT_ONE,
    leftOut: 1,
    sources: [{ kind: 'Usage', who: null, at: null, line: 'Usage · Example Client · August', signalId: null }],
    notRead: [],
  },
];

/** Long enough that "Reading what Cobrain has on that…" is seen, as it is before a real answer. */
export const EXAMPLE_ANSWER_MS = 700;

/**
 * An answerer for the example's ask box, in the shape of the real `ask` and touching nothing: no request, no store, no model. Whatever
 * is typed, it answers with the made-up answers above in turn, so a second question shows the line under an answer.
 */
export function exampleAnswerer(delayMs: number = EXAMPLE_ANSWER_MS): (store: string, question: string) => Promise<AskResult> {
  let asked = 0;
  return () => {
    const turn = EXAMPLE_ANSWERS[asked % EXAMPLE_ANSWERS.length]!;
    asked += 1;
    return new Promise((resolve) => { setTimeout(() => resolve({ store: EXAMPLE_STORE, item: null, ...turn }), delayMs); });
  };
}

/** Whether this page was asked for as the example (`/copilot?example`). */
export function wantsExample(search: string): boolean {
  return new URLSearchParams(search).has('example');
}
